import React, { useMemo, useState } from 'react';
import { MapPin, ExternalLink, Phone, ChevronDown, ChevronUp, ChevronLeft, Star, Clock } from 'lucide-react';
import { buttonVariants } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { googleMapsPlaceUrl } from '@/lib/seo';
import { branchCountLabel } from '@/lib/arabicNumerals';
import { usePostHogTracking } from '@/hooks/usePostHogTracking';
import type { StoreEventProps } from '@/lib/posthog';

// Phase 32 / BRNCH-01 — the public branches surface (UI-SPEC S1, D-22).
//
// Mirrors the PUBLIC `StoreBranch` shape from shared/src/store.ts. Rating and review count are
// public since the D-04 amendment; the two stats timestamps remain admin-only and nothing here
// may render them.
//
// Two surfaces, one row renderer:
//   * StoreBranchesQuickGlance — what visitors see. A single «الفروع» row inside the Quick Details
//     panel; the list opens in a sheet (bottom on mobile, side on desktop). A full-width card of
//     expanded rows was too heavy on both breakpoints, so the list is behind one tap.
//   * StoreBranchesCard — the original card, still mounted in the admin edit-mode grid where a
//     always-open list is what an editor wants.
//
// Two deliberate divergences from the sibling StoreLocationCard:
//   1. Empty input renders NOTHING (early `return null`) — no dashed placeholder. D-22, locked.
//   2. The map link is a real crawlable <a>, not an onClick handler — this page is server-rendered
//      for SEO, so the destination must exist in the server HTML.
type StoreBranchView = {
  name: string;
  googlePlaceId?: string;
  isPrimary?: boolean;
  address?: string;
  governorate?: string;
  phone?: string;
  workingHours?: string[];
  geometry?: { lat: number; lng: number };
  mapsUrl?: string;
  // Promoted to public during the phase-32 UAT — see the D-04 amendment note on the shared
  // StoreBranch type. The two stats timestamps stay admin-only and must not be rendered here.
  googleRating?: number;
  googleReviewCount?: number;
};

type StoreBranchesCardProps = {
  branches?: StoreBranchView[];
  trackingProps?: StoreEventProps;
};

// UI-SPEC S1 interaction contract: render the first 5 rows, everything past that hides behind
// the expand toggle. Below this count no toggle renders at all.
const COLLAPSED_ROW_COUNT = 5;

// Sort a COPY — never mutate the prop. Primary first, then the remaining branches in stored
// order (D-22). `splice`/`unshift` on the copy preserves the relative order of the rest.
const useOrderedBranches = (branches?: StoreBranchView[]): StoreBranchView[] =>
  useMemo<StoreBranchView[]>(() => {
    if (!Array.isArray(branches)) return [];
    const list = branches.filter((branch) => branch && typeof branch.name === 'string');
    const primaryIndex = list.findIndex((branch) => branch.isPrimary === true);
    if (primaryIndex > 0) {
      const copy = [...list];
      const [primary] = copy.splice(primaryIndex, 1);
      copy.unshift(primary);
      return copy;
    }
    return list;
  }, [branches]);


// ─── Shared row renderer ──────────────────────────────────────────────────────

const BranchRow: React.FC<{
  branch: StoreBranchView;
  isPrimaryRow: boolean;
  onMapClick?: () => void;
}> = ({ branch, isPrimaryRow, onMapClick }) => {
  // Hours are the bulk of a branch row and the least-scanned part of it, so they collapse behind
  // a disclosure. Everything a visitor judges a branch on — address, rating, review count — stays
  // visible without a click.
  const [hoursOpen, setHoursOpen] = useState<boolean>(false);
  const hours = Array.isArray(branch.workingHours) ? branch.workingHours : [];
  const hasRating = typeof branch.googleRating === 'number';
  const hasReviewCount = typeof branch.googleReviewCount === 'number';

  return (
    <div className="space-y-2 border-t border-gray-200 pt-4 first:border-t-0 first:pt-0">
      {/* Name + primary badge */}
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-base font-semibold text-gray-900 font-arabic">{branch.name}</h4>
        {isPrimaryRow && (
          <Badge className="border-transparent bg-warmOrange-50 text-warmOrange-700 hover:bg-warmOrange-50 font-arabic">
            الفرع الرئيسي
          </Badge>
        )}
      </div>

      {/* Governorate */}
      {branch.governorate && (
        <div>
          <Badge variant="secondary" className="font-arabic">
            {branch.governorate}
          </Badge>
        </div>
      )}

      {/* Address */}
      {branch.address && (
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-gray-500 mt-1 flex-shrink-0" />
          <p className="text-sm text-gray-600 font-arabic leading-[1.5]">{branch.address}</p>
        </div>
      )}

      {/* Rating + review count — Google Places data, public since the D-04 amendment. Numerals
          are forced LTR so "4.5" and "53" read correctly inside the RTL row. */}
      {(hasRating || hasReviewCount) && (
        <div className="flex items-center gap-1.5 text-sm text-gray-700">
          {hasRating && (
            <>
              <Star className="w-4 h-4 flex-shrink-0 fill-warmOrange-500 text-warmOrange-500" aria-hidden="true" />
              <span dir="ltr">{branch.googleRating!.toFixed(1)}</span>
            </>
          )}
          {hasRating && hasReviewCount && <span className="text-gray-400">·</span>}
          {hasReviewCount && (
            <span className="font-arabic text-gray-600">
              <span dir="ltr">{branch.googleReviewCount}</span> مراجعة
            </span>
          )}
        </div>
      )}

      {/* Phone — LTR inside the RTL surface */}
      {branch.phone && (
        <a
          href={`tel:${branch.phone.replace(/[^0-9+]/g, "")}`}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <Phone className="w-4 h-4 flex-shrink-0" />
          <span className="font-arabic" dir="ltr">
            {branch.phone}
          </span>
        </a>
      )}

      {/* Working hours — collapsed by default, full list on click. No 3-line slice any more:
          once the list is behind a disclosure there is no reason to truncate it. */}
      {hours.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setHoursOpen((previous) => !previous)}
            aria-expanded={hoursOpen}
            className="flex items-center gap-1 text-sm font-semibold text-gray-900 font-arabic hover:text-gray-700"
          >
            <Clock className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            ساعات العمل
            {hoursOpen ? (
              <ChevronUp className="w-4 h-4" aria-hidden="true" />
            ) : (
              <ChevronDown className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
          {hoursOpen && (
            <div className="mt-2 space-y-1">
              {hours.map((line, hourIndex) => (
                <p key={hourIndex} className="text-xs text-gray-600 font-arabic leading-[1.5]">
                  {line}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Map link — built from googlePlaceId against a hard-coded prefix (T-32-17).
          The stored mapsUrl is never used as an href. Omitted when there is no place id. */}
      {branch.googlePlaceId && (
        <a
          href={googleMapsPlaceUrl(branch.googlePlaceId)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onMapClick}
          className={buttonVariants({ variant: 'outline', size: 'sm' })}
        >
          <ExternalLink className="w-4 h-4 ml-2" />
          <span className="font-arabic">عرض على الخريطة</span>
        </a>
      )}
    </div>
  );
};

// ─── Public surface: one Quick Details row + a sheet ──────────────────────────

export const StoreBranchesQuickGlance: React.FC<StoreBranchesCardProps> = ({
  branches,
  trackingProps,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const ordered = useOrderedBranches(branches);
  const { trackStore } = usePostHogTracking();

  // D-22: nothing to show means nothing renders.
  if (ordered.length === 0) {
    return null;
  }

  const hasFlaggedPrimary = ordered.some((branch) => branch.isPrimary === true);

  // `useIsMobile` is false during SSR and corrects after mount. The sheet starts closed, so the
  // side is only ever read post-hydration — no mismatch in the server HTML.
  // RTL page: the inline-end edge is the left one, so a desktop side panel enters from the left.
  const side = isMobile ? 'bottom' : 'left';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: 0,
          border: 'none',
          background: 'none',
          font: 'inherit',
          fontFamily: 'var(--ys-font)',
          fontSize: 13,
          color: 'var(--ys-primary)',
          cursor: 'pointer',
        }}
      >
        {branchCountLabel(ordered.length)}
        <ChevronLeft style={{ width: 14, height: 14 }} aria-hidden="true" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side={side}
          dir="rtl"
          className={
            isMobile
              ? 'max-h-[85vh] overflow-y-auto rounded-t-2xl sm:max-w-none'
              : 'w-full overflow-y-auto sm:max-w-md'
          }
        >
          <SheetHeader className="text-start">
            <SheetTitle className="flex items-center gap-2 font-arabic">
              <MapPin className="w-5 h-5" aria-hidden="true" />
              الفروع
              <span className="text-sm font-normal text-gray-600 font-arabic">
                ({ordered.length})
              </span>
            </SheetTitle>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {ordered.map((branch, index) => (
              <BranchRow
                key={branch.googlePlaceId || `${branch.name}-${index}`}
                branch={branch}
                isPrimaryRow={branch.isPrimary === true || (!hasFlaggedPrimary && index === 0)}
                onMapClick={() =>
                  trackStore.contactClicked({
                    platform: 'google_maps',
                    surface: 'branches_card',
                    branch_index: index,
                    ...trackingProps,
                  })
                }
              />
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

// ─── Admin edit-mode surface: the always-open card ────────────────────────────

export const StoreBranchesCard: React.FC<StoreBranchesCardProps> = ({
  branches,
  trackingProps,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const ordered = useOrderedBranches(branches);
  const { trackStore } = usePostHogTracking();

  // D-22 / UI-SPEC S1: nothing to show means nothing renders. No placeholder, no empty state.
  if (ordered.length === 0) {
    return null;
  }

  // When no branch carries the flag, the first row is treated as primary for display only.
  const hasFlaggedPrimary = ordered.some((branch) => branch.isPrimary === true);
  const visible = expanded ? ordered : ordered.slice(0, COLLAPSED_ROW_COUNT);
  const hasToggle = ordered.length > COLLAPSED_ROW_COUNT;

  return (
    <Card className="border border-gray-200 bg-white">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-gray-900 font-arabic">
          <MapPin className="w-5 h-5" />
          الفروع
          <span className="text-sm text-gray-600 font-arabic">({ordered.length})</span>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {visible.map((branch, index) => (
          <BranchRow
            key={branch.googlePlaceId || `${branch.name}-${index}`}
            branch={branch}
            isPrimaryRow={branch.isPrimary === true || (!hasFlaggedPrimary && index === 0)}
            onMapClick={() =>
              trackStore.contactClicked({
                platform: 'google_maps',
                surface: 'branches_card',
                branch_index: index,
                ...trackingProps,
              })
            }
          />
        ))}

        {/* Expand toggle — the second and last accent-colored element on this card */}
        {hasToggle && (
          <button
            type="button"
            onClick={() => setExpanded((previous) => !previous)}
            className="flex w-full items-center justify-center gap-1 pt-2 text-sm font-semibold text-warmOrange-700 font-arabic hover:text-warmOrange-800"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                عرض أقل
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                عرض كل الفروع ({ordered.length})
              </>
            )}
          </button>
        )}
      </CardContent>
    </Card>
  );
};

export default StoreBranchesCard;
